import { useChatPostAttachmentApi } from "@/attachment-api/chatPostAttachmentApi"
import { useUpdateMessageContent } from "@/orval/gen"
import { ChatPostDto, UpdateChatPostParam } from "@/orval/gen/model"
import {
  ImageMIMETypes,
  useDefaultFileDialog,
} from "@/routes/-components/useDefaultFileDialog"
import { TextEditor } from "@/routes/app/-components/TextEditor"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { CancelButton, SaveButton } from "@components/Buttons"
import { DefaultModal } from "@components/default-modal"
import { DefaultError } from "@components/error-context"
import { FormButtonGroup } from "@components/form-button-group"
import {
  ActionIcon,
  AspectRatio,
  Center,
  ModalProps,
  Stack,
  TextInput,
  Text,
  Image,
  Textarea,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { IconX, IconPhotoOff } from "@tabler/icons-react"
import { useTranslation } from "react-i18next"

interface Props extends ModalProps {
  chatPostDto: ChatPostDto
  isComment?: boolean
}

export function EditFeedPostModal({
  chatPostDto,
  isComment = false,
  ...props
}: Props) {
  const { t } = useTranslation("app_server")
  const { mutateAsync: updatePost } = useUpdateMessageContent({
    mutation: {
      onSuccess: props.onClose,
      onError: (err) => {
        showErrorNotification(err as DefaultError)
      },
    },
  })
  const form = useForm<UpdateChatPostParam>({
    mode: "uncontrolled",
    initialValues: {
      chatPostId: chatPostDto.chatPostId,
      newMessage: chatPostDto.postText,
      title: isComment ? null : chatPostDto.title,
    },
  })

  const { getFileUrl, deleteAttachmentAndMetadata, addAttachmentAndMetadata } =
    useChatPostAttachmentApi()

  const attachment = chatPostDto.attachments.at(0)

  const [attachmentUrl, setAttachmentUrl] = React.useState<string>()
  React.useEffect(() => {
    if (attachment) {
      const url = getFileUrl(attachment.attachmentId, chatPostDto.chatPostId)
      setAttachmentUrl(url)
    }
  }, [attachment])

  const handleSumbit = async (formValues: UpdateChatPostParam) => {
    await updatePost({ data: formValues })
    if ((attachment && files) || (attachment && !attachmentUrl)) {
      await deleteAttachmentAndMetadata(
        attachment.attachmentId,
        chatPostDto.chatPostId
      )
    }
    if (file) {
      await addAttachmentAndMetadata(
        file,
        { chatPostId: chatPostDto.chatPostId },
        chatPostDto.chatPostId
      )
    }
  }

  const { files, fileDialog, setFiles } = useDefaultFileDialog({
    acceptedMIMETypes: ImageMIMETypes,
    multiple: false,
    maxSize: 10,
  })

  const file = files?.at(0)

  return (
    <DefaultModal
      title={isComment ? t("EditComment") : t("EditPost")}
      {...props}
    >
      <form onSubmit={form.onSubmit(handleSumbit)}>
        <Stack onClick={(evt) => evt.stopPropagation()}>
          {!isComment ? (
            <>
              <TextInput
                required
                maxLength={150}
                label={t("Title")}
                key={form.key("title")}
                {...form.getInputProps("title")}
              />
              <AspectRatio
                ratio={16 / 9}
                onClick={fileDialog.open}
                style={{ cursor: "pointer" }}
                pos={"relative"}
              >
                {(file || attachmentUrl) && (
                  <ActionIcon
                    pos={"absolute"}
                    style={{ zIndex: 9999 }}
                    top={0}
                    right={0}
                    w={"auto"}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (file) setFiles(null)
                      if (!file && attachment) setAttachmentUrl(undefined)
                    }}
                  >
                    <IconX />
                  </ActionIcon>
                )}
                {file ? (
                  <Image
                    fit="contain"
                    w="100%"
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                  />
                ) : attachmentUrl ? (
                  <Image src={attachmentUrl} />
                ) : (
                  <Center>
                    <IconPhotoOff />
                    <Text>{t("CoverImage")}</Text>
                  </Center>
                )}
              </AspectRatio>

              <TextEditor
                value={form.getValues().newMessage}
                key={form.key("newMessage")}
                {...form.getInputProps("newMessage")}
              />
            </>
          ) : (
            <Textarea
              autosize
              key={form.key("newMessage")}
              {...form.getInputProps("newMessage")}
            />
          )}
          <FormButtonGroup>
            <CancelButton onClick={props.onClose} />
            <SaveButton />
          </FormButtonGroup>
        </Stack>
      </form>
    </DefaultModal>
  )
}
